import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { OnboardingInfoFe } from "@intelligems/ig-types/fe-only/onboarding";
import { detectInstallChannelFromSource } from "@intelligems/ig-utils/install-channel";
import { CircularProgress, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useSubscriptionVersion } from "@src/api/intelligems/get-live-prices";
import { useGetOnboardingInfo } from "@src/api/organization-actions/get-onboarding-info";
import { usePostOnboardingInfo } from "@src/api/organization-actions/post-onboarding-info";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { getSubscriptionMsa } from "@src/api/subscriptions/get-subscription-msa";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { usePatchUser } from "@src/api/users/patch-user";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { OnboardingGoalsInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingGoalsInfo";
import { OnboardingSalesInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingSalesInfo";
import { OnboardingStoreInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingStoreInfo";
import { OnboardingUserInfo } from "@src/components/MainApp/Onboarding/pages/OnboardingUserInfo";
import {
  getMixpanelStepData,
  getMixpanelStepTitle,
  getOnboardingInfoPayload,
  getOrderVolumeValue,
  isContinueDisabled,
} from "@src/components/MainApp/Onboarding/utils";
import { StartTrialStep } from "@src/features/getStarted/StartTrialStep/StartTrialStep";
import { useGetStartedEnabled } from "@src/features/getStarted/hooks/useGetStartedEnabled";
import { useDialog } from "@src/hooks/dialog";
import { mpOnboardingStepComplete } from "@src/utils/mixpanel";

import { OnboardingHeaderStepper } from "./components/OnboardingHeaderStepper";
import { OnboardingWelcome } from "./pages/OnboardingWelcome";

export const OnboardingDialog = () => {
  const dialog = useDialog("onboarding");
  // New-org gate: older orgs fall back to the legacy onboarding exit.
  const getStartedEnabled = useGetStartedEnabled();
  const connectToShopifyDialog = useDialog("connectToShopify");
  const user = useLoggedInUser();
  const patchUser = usePatchUser();
  const postOnboardingInfo = usePostOnboardingInfo();
  const org = useGetOrg();
  const navigate = useNavigate();

  // Detect install channel for tracking
  const installChannel = useMemo(() => {
    return detectInstallChannelFromSource(org.data?.org?.installSource);
  }, [org.data?.org?.installSource]);

  // For backwards compatibility - track if it's a sales channel install
  const isSalesInstall = installChannel === "sales_channel";

  const priceVersion = useSubscriptionVersion();

  const theme = useTheme();
  const [currentStep, setCurrentStep] = useImmer(0);
  const [showWelcome, setShowWelcome] = useImmer(false);
  const existingOnboardingInfo = useGetOnboardingInfo();
  const [completedStepsMixpanel, setCompletedStepsMixpanel] = useImmer<
    number[]
  >([]);
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const subscriptionMsa = getSubscriptionMsa(
    "onboarding",
    currentStep === 3 ? "Continue" : "Get Started"
  );

  const [data, setData] = useImmer<OnboardingInfoFe>({
    preferredName: "",
    preferredEmail: "",
    companyRole: null,
    otherRole: "",
    teamDescription: null,
    agencyPrimaryOperator: null,
    previousExperience: null,
    storeType: "standard",
    userReportedOrderCount: "I don't know",
    productionStoreUrl: "",
    goals: [],
    focus: null,
    buyingIntention: null,
    otherBuyingIntention: "",
    sourceAttribution: null,
    otherSourceAttribution: "",
  });

  useEffect(() => {
    if (user.isSuccess) {
      setData({
        ...data,
        preferredEmail: user.data.user.email,
        preferredName: `${user.data.user.firstName} ${user.data.user.lastName}`,
        userReportedOrderCount: getOrderVolumeValue(
          org.data.org.orderCount || 0
        ),
      });
    }
  }, [user.isSuccess]);

  const handleChange = (
    field: keyof OnboardingInfoFe,
    value: string | null | undefined | string[]
  ) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const onComplete = ({
    bypassOnboardingInfo,
  }: {
    bypassOnboardingInfo: boolean;
  }) => {
    if (user.isSuccess && org.isSuccess) {
      if (!bypassOnboardingInfo) {
        postOnboardingInfo.mutate({
          body: getOnboardingInfoPayload(data),
          pathParams: undefined,
          queryParams: undefined,
        });
      }
      patchUser.mutate({
        queryParams: undefined,
        pathParams: { id: user.data.user.id },
        body: {
          user: {
            organizationId: org.data.org.id,
            id: user.data.user.id,
            showWelcome: false,
            preferredName: data.preferredName,
            preferredEmail: data.preferredEmail,
            companyRole:
              data.companyRole === "Other" ? data.otherRole : data.companyRole,
            previousExperience: data.previousExperience,
            email: user.data.user.email,
          },
        },
      });
      // Older orgs: legacy exit — close and open Connect-to-Shopify.
      if (!getStartedEnabled) {
        dialog.close();
        connectToShopifyDialog.open();
        return;
      }
      setShowWelcome(true);
    }
  };

  const handleWelcomeGetStarted = () => {
    setShowWelcome(false);
    dialog.close();
    // Hand off intent only — the get-started machine opens the current phase's
    // action step from ?start; it, not onboarding, decides which view shows.
    void navigate("/get-started?start=1", { replace: true });
  };

  // The self-serve picker creates the trial itself, so we finish onboarding
  // without firing any billing call of our own. Skipping leaves the org
  // untrialed; the Get Started flow then surfaces the trial step.
  const completeAfterTrialStep = (buttonClicked: string) => {
    mpOnboardingStepComplete(getMixpanelStepTitle(4), 4, {
      button_clicked: buttonClicked,
      is_sales_install: isSalesInstall,
      install_channel: installChannel,
    });
    onComplete({ bypassOnboardingInfo: false });
  };

  const isContinueDisabledWrapper = useMemo(() => {
    return isContinueDisabled(data, currentStep);
  }, [currentStep, data]);

  const showHeaderStepper =
    priceVersion === "v3" &&
    currentStep !== 4 &&
    !existingOnboardingInfo.data &&
    !isSubscriptionActive;

  // Step 4 is the self-serve trial picker; it renders full-bleed with its own
  // actions instead of the shared footer.
  const isTrialStep = currentStep === 4;

  const onContinueClick = () => {
    // if already has onboarding info OR on legacy pricing OR has active subscription, skip all other steps
    if (
      (existingOnboardingInfo.data !== null &&
        existingOnboardingInfo.data !== undefined) ||
      priceVersion !== "v3" ||
      isSubscriptionActive
    ) {
      mpOnboardingStepComplete(
        getMixpanelStepTitle(currentStep),
        currentStep,
        {
          ...getMixpanelStepData(data, currentStep),
          is_sales_install: isSalesInstall,
          install_channel: installChannel,
        },
        existingOnboardingInfo.data !== null &&
          existingOnboardingInfo.data !== undefined
          ? "existing_onboarding_data"
          : priceVersion !== "v3"
            ? `legacy_pricing_${priceVersion}`
            : isSubscriptionActive
              ? "subscription_active"
              : undefined
      );
      onComplete({ bypassOnboardingInfo: true });
    } else {
      if (!completedStepsMixpanel.includes(currentStep)) {
        setCompletedStepsMixpanel((prev) => {
          prev = [...prev, currentStep];
          return prev;
        });
        mpOnboardingStepComplete(
          getMixpanelStepTitle(currentStep),
          currentStep,
          {
            ...getMixpanelStepData(data, currentStep),
            is_sales_install: isSalesInstall,
            install_channel: installChannel,
          }
        );
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <>
      <IgDialog
        minWidth={"800px"}
        dialogContentPadding={"0px"}
        dialogContentWidth={"100%"}
        padding={isTrialStep ? "0px" : "32px"}
        dontUseDialogContent={isTrialStep}
        disableEscapeKeyDown={true}
        title={null}
        hideActions={true}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: theme.igBackgroundColors.baseLightMid,
          },
          "& .MuiPaper-root": {
            boxShadow: theme.igShadows.low,
            // The trial step is full-bleed, so clip its square corners to the
            // dialog's rounded paper instead of stacking a card inside a card.
            ...(isTrialStep ? { overflow: "hidden" } : {}),
          },
        }}
        handleClose={(reason) => {
          if (reason !== "backdropClick") {
            dialog.close();
          }
        }}
        showDialog={dialog.isOpen}
        rawContent={
          existingOnboardingInfo.isLoading || org.isLoading ? (
            <Grid
              container
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              sx={{ height: "100px" }}
            >
              <CircularProgress color={"info"} />
            </Grid>
          ) : (
            <Stack direction={"column"} spacing={8}>
              {showHeaderStepper && (
                <OnboardingHeaderStepper currentStep={currentStep} />
              )}

              {currentStep === 0 ? (
                <OnboardingUserInfo data={data} onChange={handleChange} />
              ) : currentStep === 1 ? (
                <OnboardingStoreInfo
                  data={data}
                  onChange={handleChange}
                  storeUrl={org.data.org.domain || org.data.org.shopId || ""}
                />
              ) : currentStep === 2 ? (
                <OnboardingGoalsInfo data={data} onChange={handleChange} />
              ) : currentStep === 3 ? (
                <OnboardingSalesInfo data={data} onChange={handleChange} />
              ) : (
                <StartTrialStep
                  embedded
                  onTrialStarted={() => {
                    completeAfterTrialStep("start_trial");
                  }}
                  onTrialSkipped={() => {
                    completeAfterTrialStep("skip_trial");
                  }}
                />
              )}
              {isTrialStep ? null : (
                <Grid
                  container
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Grid size="auto">
                    {currentStep !== 0 && (
                      <IgButton
                        onClick={() => {
                          setCurrentStep((prev) => prev - 1);
                        }}
                        color={"secondary"}
                      >
                        Back
                      </IgButton>
                    )}
                  </Grid>
                  <Grid
                    container
                    direction={"row"}
                    alignItems={"flex-end"}
                    columnSpacing={3}
                    size="auto"
                  >
                    <Grid>
                      <IgTooltip title={isContinueDisabledWrapper.reason}>
                        <IgButton
                          disabled={isContinueDisabledWrapper.isDisabled}
                          onClick={onContinueClick}
                          sx={{ minWidth: 165 }}
                        >
                          Continue
                        </IgButton>
                      </IgTooltip>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {currentStep === 3 && subscriptionMsa}
            </Stack>
          )
        }
      />
      <OnboardingWelcome
        open={showWelcome}
        onGetStarted={handleWelcomeGetStarted}
      />
    </>
  );
};
